import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ValidationSummary = ({ errors = {}, warnings = {}, onFieldFocus }) => {
  const errorCount = Object.keys(errors)?.length;
  const warningCount = Object.keys(warnings)?.length;
  const totalIssues = errorCount + warningCount;

  if (totalIssues === 0) {
    return (
      <div className="bg-success/10 border border-success/20 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <Icon name="CheckCircle" size={20} className="text-success" />
          <div>
            <h4 className="font-medium text-success">All Fields Valid</h4>
            <p className="text-sm text-success/80">Your contract information is complete and valid.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleFieldClick = (fieldName) => {
    if (onFieldFocus) {
      onFieldFocus(fieldName);
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <div className="bg-error/10 border border-error/20 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <Icon name="AlertCircle" size={20} className="text-error" />
          <div>
            <h4 className="font-medium text-error">
              {totalIssues} Issue{totalIssues > 1 ? 's' : ''} Found
            </h4>
            <p className="text-sm text-error/80">
              {errorCount > 0 && `${errorCount} error${errorCount > 1 ? 's' : ''}`}
              {errorCount > 0 && warningCount > 0 && ' and '}
              {warningCount > 0 && `${warningCount} warning${warningCount > 1 ? 's' : ''}`}
              {' '}need attention before saving.
            </p>
          </div>
        </div>
      </div>
      {/* Error Details */}
      {errorCount > 0 && (
        <div className="bg-card border border-error/20 rounded-lg">
          <div className="p-4 border-b border-border">
            <div className="flex items-center space-x-2">
              <Icon name="XCircle" size={16} className="text-error" />
              <h5 className="font-medium text-error">Errors ({errorCount})</h5>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {Object.entries(errors)?.map(([field, message]) => (
              <button
                key={field}
                onClick={() => handleFieldClick(field)}
                className="flex items-start space-x-3 w-full text-left p-2 rounded-md hover:bg-muted transition-legal-fast"
              >
                <Icon name="AlertCircle" size={14} className="text-error mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground capitalize">
                    {field?.replace(/([A-Z])/g, ' $1')?.replace(/^./, str => str?.toUpperCase())}
                  </p>
                  <p className="text-xs text-error">{message}</p>
                </div>
                <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Warning Details */}
      {warningCount > 0 && (
        <div className="bg-card border border-warning/20 rounded-lg">
          <div className="p-4 border-b border-border">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <h5 className="font-medium text-warning">Warnings ({warningCount})</h5>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {Object.entries(warnings)?.map(([field, message]) => (
              <button
                key={field}
                onClick={() => handleFieldClick(field)}
                className="flex items-start space-x-3 w-full text-left p-2 rounded-md hover:bg-muted transition-legal-fast"
              >
                <Icon name="AlertTriangle" size={14} className="text-warning mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground capitalize">
                    {field?.replace(/([A-Z])/g, ' $1')?.replace(/^./, str => str?.toUpperCase())}
                  </p>
                  <p className="text-xs text-warning">{message}</p>
                </div>
                <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Quick Actions */}
      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
        <p className="text-sm text-muted-foreground">
          Fix all issues to enable saving and continue to the next step.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleFieldClick(Object.keys(errors)?.[0] || Object.keys(warnings)?.[0])}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Fix First Issue
        </Button>
      </div>
    </div>
  );
};

export default ValidationSummary;